import React from 'react';
import {
  Box,
  Avatar,
  Button,
  Input,
  Typography,
  Alert,
  IconButton
} from '@mui/material';
import {
  CloudUpload,
  Delete as DeleteIcon
} from '@mui/icons-material';
// Utility function to fix S3 URL format
const fixS3Url = (url: string | undefined): string | undefined => {
  if (!url) return undefined;

  // Check if it's an S3 URL with regional endpoint
  const s3RegionalPattern = /https:\/\/([^.]+)\.s3\.([^.]+)\.amazonaws\.com\/(.+)/;
  const match = url.match(s3RegionalPattern);

  if (match) {
    const bucketName = match[1];
    const path = match[3];

    // Convert to path-style URL (works better with CORS)
    const fixedUrl = `https://${bucketName}.s3.amazonaws.com/${path}`;
    // console.log('🔧 Fixed S3 URL:', url, '->', fixedUrl);
    return fixedUrl;
  }

  return url;
};

// Utility function to get cache-busted image URL
const getCacheBustedImageUrl = (url: string | undefined): string | undefined => {
  if (!url) return undefined;

  const fixedUrl = fixS3Url(url);
  if (!fixedUrl) return undefined;

  const ts = Date.now();
  return `${fixedUrl}?t=${ts}`;
};

interface ProfilePictureUploadProps {
  profileImage?: string;
  onProfilePictureChange: (file: File | null) => void;
  onProfilePictureDelete: () => void;
  uploading?: boolean;
  error?: string;
  success?: string;
  size?: number;
  label?: string;
  description?: string;
  showDelete?: boolean;
}

export const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({
  profileImage,
  onProfilePictureChange,
  onProfilePictureDelete,
  uploading = false,
  error,
  success,
  size = 120,
  label = 'Upload Profile Picture',
  description = 'Upload your profile picture. Maximum file size: 5MB. Supported formats: JPG, PNG, GIF.',
  showDelete = true
}) => {
  const handleProfilePictureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please upload a valid image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }

      onProfilePictureChange(file);

      // Reset the input value to allow re-uploading the same file
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const getProfilePicturePreview = (picture: string | undefined) => {
    if (!picture) return undefined;
    return getCacheBustedImageUrl(picture);
  };

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      mb: 3,
      p: 3,
      border: `2px dashed ${error ? 'error.main' : 'primary.main'}`,
      borderRadius: 2,
      bgcolor: 'background.paper'
    }}>
      <Avatar
        src={getProfilePicturePreview(profileImage)}
        imgProps={{
          crossOrigin: "anonymous",
          onError: (e) => {
            console.error('❌ Profile picture upload avatar failed to load:', profileImage);
            console.error('Error event:', e);
            console.error('This is likely a CORS or S3 permissions issue');
            // Try to fetch the image to get more details
            fetch(profileImage || '', { mode: 'no-cors' })
              .then(() => console.log('✅ Image exists but CORS is blocking it'))
              .catch(err => console.error('❌ Image fetch error:', err));
          }
        }}
        sx={{
          width: size,
          height: size,
          bgcolor: profileImage ? 'transparent' : 'primary.main',
          fontSize: size > 120 ? '3rem' : '2rem',
          border: profileImage ? '2px solid #e0e0e0' : 'none'
        }}
      >
        {(!profileImage && 'U') || ''}
      </Avatar>

      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle1" gutterBottom>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {description}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Input
            type="file"
            inputProps={{ accept: 'image/*' }}
            onChange={handleProfilePictureChange}
            sx={{ display: 'none' }}
            id="profile-picture-input"
          />
          <label htmlFor="profile-picture-input">
            <Button
              variant="contained"
              component="span"
              startIcon={<CloudUpload />}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Picture'}
            </Button>
          </label>

          {showDelete && profileImage && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={onProfilePictureDelete}
              disabled={uploading}
            >
              Remove Picture
            </Button>
          )}
        </Box>

        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 1 }}>
            {success}
          </Alert>
        )}
      </Box>
    </Box>
  );
};